import React from 'react';
import {useDispatch} from 'react-redux';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    PrimaryButton,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import {toast} from 'react-toastify';
import api, {LIST, STOREFRONT_SETTINGS} from '../../../store/Api';
import {
    assistantErrorMessage,
    useCancelAssistantSessionWithReasonMutation,
} from '../../../store/endpoints/AssistantEndpoints';
import {AssistantCancelReasonCode} from '../../../models/Assistant';
import {CANCEL_REASON_CHOICES, cancelSurveyBody} from './aiStorefrontCreateLogic';

interface IAiCancelSurveyDialogProps {
    isOpen: boolean;
    sessionId: string;
    /**
     * Which storefront this removes, e.g. `nutmegit.com (nutmegv8)`. An organization can have several
     * near-identical builds going, and the one certainty this dialog owes the operator is WHICH one
     * dies when they confirm.
     */
    storefrontLabel?: string;
    /** Back out of starting over; the generation is untouched. */
    onDismiss: () => void;
    /** The session was cancelled and its storefront removed. */
    onCancelled: () => void;
}

const CANCEL_FAILED = 'The storefront could not be removed. Please try again.';

/**
 * The "why are you starting over" survey.
 *
 * The answer is worth having and the cancel is not worth holding hostage for it, so both footer
 * buttons cancel: Skip sends an empty body, the submit sends whatever was answered. Nothing here is
 * ever required and nothing is ever disabled on validity.
 */
const AiCancelSurveyDialog: React.FC<IAiCancelSurveyDialogProps> = (props) => {
    const dispatch = useDispatch();
    const [cancelSession] = useCancelAssistantSessionWithReasonMutation();
    const [reasonCode, setReasonCode] = React.useState<AssistantCancelReasonCode | undefined>();
    const [comments, setComments] = React.useState('');
    const [cancelling, setCancelling] = React.useState(false);

    React.useEffect(() => {
        if (!props.isOpen) {
            setReasonCode(undefined);
            setComments('');
            setCancelling(false);
        }
    }, [props.isOpen]);

    const cancel = async (withSurvey: boolean) => {
        setCancelling(true);
        const toastId = toast.loading('Removing the storefront...');
        const body = withSurvey ? cancelSurveyBody(reasonCode, comments) : {};
        try {
            const result = await cancelSession({id: props.sessionId, ...body}).unwrap();
            if (result?.success === false) {
                toast.update(toastId, {
                    type: 'error',
                    render: result.message ?? CANCEL_FAILED,
                    isLoading: false,
                    autoClose: 4000,
                });
                setCancelling(false);
                return;
            }
            toast.update(toastId, {
                type: 'success',
                render: 'The storefront was removed',
                isLoading: false,
                autoClose: 3000,
            });
            dispatch(api.util.invalidateTags([{type: STOREFRONT_SETTINGS, id: LIST}]));
            props.onCancelled();
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: assistantErrorMessage(error, CANCEL_FAILED),
                isLoading: false,
                autoClose: 4000,
            });
            setCancelling(false);
        }
    };

    return (
        <Dialog
            hidden={!props.isOpen}
            onDismiss={() => {
                if (!cancelling) props.onDismiss();
            }}
            minWidth={460}
            dialogContentProps={{
                title: 'Help us improve',
                subText: 'Why are you starting over? Answering is optional.',
                showCloseButton: true,
            }}
            modalProps={{isBlocking: true}}
        >
            <Stack tokens={{childrenGap: '1em'}}>
                <Stack horizontal wrap tokens={{childrenGap: '0.5em'}}>
                    {CANCEL_REASON_CHOICES.map((choice) => (
                        <DefaultButton
                            key={choice.key}
                            text={choice.text}
                            toggle
                            checked={reasonCode === choice.key}
                            disabled={cancelling}
                            // Tapping the picked chip again clears it, so a mis-tap is not a
                            // permanent answer on a survey with no clear button.
                            onClick={() => setReasonCode((prev) => (prev === choice.key ? undefined : choice.key))}
                        />
                    ))}
                </Stack>

                <TextField
                    label='Anything else?'
                    multiline
                    rows={3}
                    value={comments}
                    disabled={cancelling}
                    placeholder='Optional. Tell us what went wrong or what you expected.'
                    onChange={(_, val) => setComments(val ?? '')}
                />

                <Text variant='small'>
                    {props.storefrontLabel
                        ? `Starting over removes the storefront ${props.storefrontLabel} and everything we built in it. Nothing else in your account changes.`
                        : 'Starting over removes the storefront we built. Nothing else in your account changes.'}
                </Text>
            </Stack>
            <DialogFooter>
                <PrimaryButton
                    text={cancelling ? 'Removing...' : 'Cancel storefront'}
                    disabled={cancelling}
                    onClick={() => cancel(true)}
                />
                <DefaultButton text='Skip' disabled={cancelling} onClick={() => cancel(false)}/>
            </DialogFooter>
        </Dialog>
    );
};

export default AiCancelSurveyDialog;
